/**
 * Turning an inventory API row into the object the wastage screen works with.
 *
 * Extracted from the inline `processedRawItems` map inside StockOutForm's
 * fetchRawItems, because that mapping had a bug nothing could see: it copied the unit
 * LABEL and dropped the unit ID.
 *
 *     unit: detailedItem?.unit || item.standardUnit || 'units'   // 'kg' — a label
 *     // ...and no standard_unit_id anywhere
 *
 * The submit path then asked for exactly the field that had been dropped:
 *
 *     unitId: actualItem?.unit_id || actualItem?.standard_unit_id || null
 *
 * Both operands were undefined, so every raw-ingredient wastage row in the database
 * was written with unit_id = NULL. Reports resolving a unit through GlobalUnits print
 * "units" instead of "kg", and the recorder's decomposition skips unit conversion
 * altogether when unitId is null — which is harmless for a kg item typed in kg and a
 * 1000x error waiting for a gram item.
 *
 * A label is for the person; an id is for the ledger. Carry both.
 */

/** The units a dish is wasted in. GlobalUnits: Plate. */
const PLATE_UNIT_ID = 22;

/**
 * The label of the Wastage chip that holds non-consumables — packaging, cutlery,
 * carry bags, tissues.
 *
 * It is a fixed label, not the category's own name, on purpose. Businesses carry the
 * same shelf under different names ('NON CONSUMABLE' from signup, 'Non Consumable'
 * from a stock-in that created it, 'Packaging' from an owner who typed their own),
 * and the chip groups them all: what the person is choosing is the KIND of thing, and
 * one kind should not be three chips. The grouping is by `category_type` from the
 * server — see the comment on that column in routes/unitMapping.js.
 */
const NON_CONSUMABLE_CATEGORY = 'Non Consumable';

/**
 * Is this inventory row a non-consumable?
 *
 * Reads `category_type` — the server's classification — from either of the two calls
 * that describe the item, because the stock-overview call is allowed to fail and the
 * answer must survive that.
 */
function isNonConsumableRow(apiItem, detailedItem) {
  const type = detailedItem?.category_type || apiItem?.category_type;
  return String(type || '').toLowerCase() === 'non_consumable';
}

/**
 * Build the wastage-screen item from the two calls that describe it: the inventory
 * list (`/unit-mapping/inventory-items/:businessId`) and the richer stock overview.
 *
 * @param {object} apiItem      row from the inventory list — carries standard_unit_id
 * @param {object} [detailedItem] matching row from the stock overview, may be absent
 *   when that second call fails; the unit id must still survive that.
 */
function buildRawWastageItem(apiItem, detailedItem) {
  const item = apiItem || {};
  const detailed = detailedItem || null;
  const detailedCategory = (detailed?.category || '').trim();

  return {
    id: `raw_${item.id}`,
    originalId: item.id,
    name: item.name,
    // Still 'Raw Items'. The Non Consumable chip is an ADDITIONAL way into the same
    // rows, not a move: an owner who has always found everything under Raw Items keeps
    // finding it there. `isNonConsumable` is what the new chip filters on.
    category: 'Raw Items',
    isNonConsumable: isNonConsumableRow(item, detailed),
    originalCategory: detailedCategory || item.category || 'Uncategorized',
    isComplimentary: detailedCategory.toLowerCase() === 'complimentary items',
    // Real-time unit_cost (already includes complimentary + current pricing).
    price: parseFloat(detailed?.unit_cost || 0),
    quantity: parseFloat(detailed?.quantity || 0),
    // The label the card shows...
    unit: detailed?.unit || item.standardUnit || 'units',
    // ...and the id the server stores. `?? null` so an absent id is an explicit null
    // rather than an undefined key that disappears from the JSON payload.
    standard_unit_id: item.standard_unit_id ?? null,
    stockLevel: detailed?.stock_level || 'adequate',
    daysToExpiry: parseInt(detailed?.days_to_expiry || 999, 10),
    nearestExpiry: detailed?.expiry_date,
    reorderPoint: detailed?.minimum_stock_level || 0,
    safetyStock: detailed?.maximum_stock_level || 0,
    batchNumber: detailed?.batch_number || 'N/A',
  };
}

/**
 * The unit id to send for one wastage row.
 *
 * @param {object} actualItem the screen's item object
 * @param {'InventoryItem'|'MenuItem'|'Preparation'} itemType
 * @returns {number|null} null means "no unit known" — deliberately not a guess, because
 *   a wrong unit converts the quantity against a scale the item is not measured in.
 */
function resolveRowUnitId(actualItem, itemType) {
  const it = actualItem || {};
  if (itemType === 'MenuItem') {
    return it.unit_id ?? it.standard_unit_id ?? PLATE_UNIT_ID;
  }
  if (itemType === 'Preparation') {
    return it.total_weight_unit_id ?? it.unit_id ?? null;
  }
  return it.unit_id ?? it.standard_unit_id ?? null;
}

export {
  buildRawWastageItem,
  resolveRowUnitId,
  isNonConsumableRow,
  PLATE_UNIT_ID,
  NON_CONSUMABLE_CATEGORY,
};
